import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService, BasketPair } from '../services/api';
import BasketPairsTable from '../components/BasketPairsTable';
import BasketProductPanel from '../components/BasketProductPanel';

type Tab = 'pairs' | 'by-sku';
type OrderBy = 'lift' | 'support' | 'confidence' | 'co_count';

const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return '-';
  try {
    return new Date(timestamp).toLocaleString('pt-PT', {
      timeZone: 'Europe/Lisbon',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return timestamp;
  }
};

// Parse defensivo: devolve fallback se o input não for número finito (evita NaN no estado).
const floatOr = (raw: string, fallback: number): number => {
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
};
const intOr = (raw: string, fallback: number): number => {
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
};

const downloadCsv = (pairs: BasketPair[], snapshot: string | null) => {
  const header = [
    'sku_a', 'name_a', 'online_a', 'sku_b', 'name_b', 'online_b', 'co_count', 'support', 'confidence_a_to_b',
    'confidence_b_to_a', 'lift', 'jaccard',
    'support_count_a', 'support_count_b', 'total_baskets', 'calculation_timestamp',
  ];
  // Formatação Excel PT: separador ';', decimal ',' nos floats, escape RFC4180 para texto.
  const fmtFloat = (v: number | null | undefined): string =>
    v === null || v === undefined ? '' : String(v).replace('.', ',');
  const fmtText = (v: any): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    // Envolver em aspas se contém separador, aspas ou quebras de linha
    return /[;"\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const rows = pairs.map((p) => [
    fmtText(p.sku_a), fmtText(p.name_a), p.online_a ? 'sim' : 'nao',
    fmtText(p.sku_b), fmtText(p.name_b), p.online_b ? 'sim' : 'nao',
    p.co_count, fmtFloat(p.support), fmtFloat(p.confidence_a_to_b), fmtFloat(p.confidence_b_to_a),
    fmtFloat(p.lift), fmtFloat(p.jaccard),
    p.support_count_a, p.support_count_b, p.total_baskets,
    fmtText(p.calculation_timestamp),
  ].join(';'));
  const csv = [header.join(';'), ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const stamp = snapshot ? snapshot.replace(/[:T]/g, '-').slice(0, 16) : 'export';
  a.href = url;
  a.download = `basket_pairs_${stamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const BasketAnalysisPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('pairs');

  // Filters for "pairs" tab
  const [minLift, setMinLift] = useState(1.5);
  const [minSupport, setMinSupport] = useState(0);
  const [minCoCount, setMinCoCount] = useState(10);
  const [orderBy, setOrderBy] = useState<OrderBy>('lift');
  const [limit, setLimit] = useState(200);

  const [pairs, setPairs] = useState<BasketPair[]>([]);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const requestRef = useRef(0);

  // Fetch desacoplado dos inputs: dispara só via botão "Aplicar" (e 1ª vez ao abrir a tab).
  const loadPairs = useCallback(async (orderOverride?: OrderBy) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.getBasketPairs({
        min_lift: minLift,
        min_support: minSupport,
        min_co_count: minCoCount,
        order_by: orderOverride ?? orderBy,
        limit,
      });
      if (reqId !== requestRef.current) return; // resposta obsoleta — ignorar
      setPairs(resp.results);
      setSnapshot(resp.calculation_timestamp);
    } catch (err: any) {
      if (reqId !== requestRef.current) return;
      setError(err?.response?.data?.error || err?.message || 'Erro a carregar pares');
      setPairs([]);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [minLift, minSupport, minCoCount, orderBy, limit]);

  // Clique num cabeçalho da tabela → reordenar no servidor (não localmente sobre
  // o top-N já truncado, que mostraria o máximo do subconjunto e não o topo real).
  const handleServerOrderChange = useCallback((order: OrderBy) => {
    setOrderBy(order);
    loadPairs(order);
  }, [loadPairs]);

  // Carrega 1× ao entrar na tab "pairs" (não recarrega ao voltar se já há dados).
  useEffect(() => {
    if (tab === 'pairs' && !loaded) {
      setLoaded(true);
      loadPairs();
    }
  }, [tab, loaded, loadPairs]);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h2 className="mb-1">🛒 Frequently Bought Together</h2>
          <p className="text-muted small mb-0">
            Pares de produtos comprados em conjunto (janela rolling 150 dias; relações por família: 60 dias). Métricas:
            <em> support, confidence, lift, jaccard</em>. Pares com lift &gt; 1 indicam associação positiva.
          </p>
          <p className="text-muted small mb-0 fst-italic">
            Nota: support e contagens são relativos a cestos com 2–20 itens (multi-produto), não à
            frequência total de vendas de cada produto.
          </p>
          {snapshot && (
            <p className="text-muted small mb-0">
              <strong>Último cálculo:</strong> {formatTimestamp(snapshot)}
            </p>
          )}
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'pairs' ? 'active' : ''}`}
            onClick={() => setTab('pairs')}
          >
            Pares globais
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === 'by-sku' ? 'active' : ''}`}
            onClick={() => setTab('by-sku')}
          >
            Por produto (SKU)
          </button>
        </li>
      </ul>

      {tab === 'pairs' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label small mb-1">Min Lift</label>
                  <input
                    type="number" step="0.1" min={1.0}
                    className="form-control"
                    value={minLift}
                    onChange={(e) => setMinLift(floatOr(e.target.value, 1.5))}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Min Support</label>
                  <input
                    type="number" step="0.0001" min={0}
                    className="form-control"
                    value={minSupport}
                    onChange={(e) => setMinSupport(floatOr(e.target.value, 0))}
                  />
                  <small className="text-muted">Fracção (ex: 0.001 = 0.1%)</small>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Min Co-Count</label>
                  <input
                    type="number" min={1}
                    className="form-control"
                    value={minCoCount}
                    onChange={(e) => setMinCoCount(intOr(e.target.value, 10))}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Ordenar por</label>
                  <select
                    className="form-select"
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value as OrderBy)}
                  >
                    <option value="lift">Lift</option>
                    <option value="support">Support</option>
                    <option value="confidence">Confidence (máx)</option>
                    <option value="co_count">Co-Count</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Limit</label>
                  <input
                    type="number" min={1} max={5000}
                    className="form-control"
                    value={limit}
                    onChange={(e) => setLimit(intOr(e.target.value, 200))}
                  />
                </div>
                <div className="col-md-2 d-flex gap-2">
                  <button className="btn btn-primary flex-grow-1" onClick={() => loadPairs()} disabled={loading}>
                    {loading ? 'A carregar...' : 'Aplicar'}
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => downloadCsv(pairs, snapshot)}
                    disabled={pairs.length === 0}
                    title="Exportar CSV (formato Excel PT)"
                  >
                    CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <BasketPairsTable
            pairs={pairs}
            loading={loading}
            serverOrder={orderBy}
            onServerOrderChange={handleServerOrderChange}
          />
        </>
      )}

      {tab === 'by-sku' && <BasketProductPanel />}

      <div className="alert alert-warning mt-3 small">
        <strong>Nota:</strong> pares dentro da mesma categoria podem indicar <em>substitutos</em>
        (produtos comparados pelo cliente) e não <em>complementos</em>. Validar manualmente antes de
        usar em campanhas de cross-sell.
      </div>
    </div>
  );
};

export default BasketAnalysisPage;
